<template>
  <DecoratorInput
    iconSize="sm"
    iconTitle="query_stats"
    label="Postprocessings"
    class="setup-postprocessing"
  >
    <div v-if="preselectedPostprocessings.length" class="setup-postprocessing__postprocessings">
      <div
        v-for="[channel] of preselectedPostprocessings"
        :key="channel"
        class="setup-postprocessing__postprocessing"
      >
        <div class="setup-postprocessing__postprocessing-info">
          {{ channelNames[channel] }}

          <Btn padding="sm" height="xs" @click="setupPostprocessing(channel)"> Edit </Btn>
          <BtnIcon iconMaterial="close" @click="removePostprocessing(channel)" />
        </div>
        <!--        <div-->
        <!--          v-if="channel === 'primary' && preselectedPostprocessings.length > 1"-->
        <!--          class="setup-postprocessing__warning"-->
        <!--        >-->
        <!--          Postprocessing for the primary channel is prioritised over others in case of conflict-->
        <!--        </div>-->
      </div>
    </div>

    <div class="setup-postprocessing__channels">
      <template v-if="unsettedChannelsWithNames.length">
        <Btn
          v-for="channel of unsettedChannelsWithNames"
          :key="channel"
          padding="sm"
          iconRight="add"
          @click="setupPostprocessing(channel)"
        >
          {{ channelNames[channel] }}
        </Btn>
      </template>
      <slot v-else-if="availableChannels.length === 0" name="noAvailableChannelsError">
        No channels to select
      </slot>

      <template v-if="$scopedSlots.note">
        <slot name="note" :availableChannels="availableChannels" />
      </template>
    </div>

    <div
      v-if="isRequired && availableChannels.length > 0 && preselectedPostprocessings.length === 0"
      class="setup-postprocessing__error"
    >
      You must configure a postprocessing
    </div>

    <Portal to="modal">
      <PostprocessingTemplateEditModal
        :postprocessingTemplate="postprocessingForEditing"
        :selectedChannel="channelForEditing"
        :isPreselectMode="isPreselectMode"
        :isCalibrationSequenceMode="isCalibrationSequenceMode"
        @update:sequencePostprocessing="save"
        @preselect="save"
      />
    </Portal>
  </DecoratorInput>
</template>

<script>
  import DecoratorInput from '@/uikitProject/decorators/DecoratorInput.vue';
  import Btn from '@/uikitBase/btns/Btn.vue';
  import PostprocessingTemplateEditModal from '@/components/block/modal/PostprocessingTemplateEditModal.vue';
  import BtnIcon from '@/uikitBase/btns/BtnIcon.vue';
  import { getDetectorParts } from '@/utils/methodHelpers';

  const EVENT_MODEL = 'model';

  export default {
    name: 'SetupPostprocessings',

    components: {
      BtnIcon,
      PostprocessingTemplateEditModal,
      Btn,
      DecoratorInput,
    },

    model: {
      prop: 'postprocessings',
      event: EVENT_MODEL,
    },

    props: {
      /**
       * Format: {
       *          D: null,
       *          D2: null,
       *          D3: null,
       *          D4: null,
       *       }
       */
      postprocessings: {
        type: Object,
        required: true,
      },
      availableChannels: {
        type: Array,
        default: () => [],
      },

      detectorConfiguration: {
        type: Object,
        default: null,
      },

      detectorPortDescription: {
        type: String,
        required: true,
      },

      isRequired: {
        type: Boolean,
      },

      isCalibrationSequenceMode: {
        type: Boolean,
      },
      isPreselectMode: {
        type: Boolean,
      },
    },

    data: () => ({
      channelForEditing: '',
      postprocessingForEditing: undefined,
    }),

    computed: {
      preselectedPostprocessings() {
        return Object.entries(this.postprocessings).filter(
          ([channel, postprocessing]) =>
            postprocessing != null && this.availableChannels.includes(channel),
        );
      },
      unsettedChannels() {
        return this.availableChannels.filter((channel) => this.postprocessings[channel] == null);
      },
      unsettedChannelsWithNames() {
        return this.unsettedChannels.filter((channel) => this.channelNames[channel]);
      },

      channelNames() {
        return getDetectorParts(this.detectorConfiguration ?? this.detectorPortDescription);
      },
    },

    methods: {
      setupPostprocessing(channel) {
        this.channelForEditing = channel;
        this.postprocessingForEditing = this.postprocessings[channel] ?? undefined;
        this.$modal.show('postprocessing-template-edit');
      },

      removePostprocessing(channel) {
        this.$emit(EVENT_MODEL, {
          ...this.postprocessings,
          [channel]: null,
        });
      },
      save(postprocessing) {
        this.$emit(EVENT_MODEL, {
          ...this.postprocessings,
          [this.channelForEditing]: postprocessing,
        });
      },
    },
  };
</script>

<style scoped lang="scss">
  .setup-postprocessing {
    &__postprocessings {
      margin-top: 5px;
    }

    &__postprocessing {
      margin-bottom: 10px;
    }

    &__postprocessing-info {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &__warning {
      margin-top: 2px;
      margin-bottom: 5px;
      color: $color-text-warning;
    }

    &__channels {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      min-height: 32px;
      align-items: center;
    }

    &__error {
      margin-top: 2px;
      color: $color-text-danger;
    }
  }
</style>
